import ElectronicAddressBadge from '@/components/invoices/ElectronicAddressBadge'
import { statusColors } from '@/constants'
import { Contract, Order, SharedProps } from '@/types'
import { formatStatus } from '@/utils/format'
import { Head, Link, router } from '@inertiajs/react'
import { Alert, Anchor, Badge, Box, Button, Group, Paper, Stack, Table, Text, Title } from '@mantine/core'
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconBook,
  IconPencil,
  IconRefresh,
  IconShoppingCart,
} from '@tabler/icons-react'
import dayjs from 'dayjs'

interface ContractShowPageProps extends SharedProps {
  contract: Contract
  orders: Order[]
  missing_fields: string[]
}

function Detail({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
        {label}
      </Text>
      <Text size="lg">{value || '—'}</Text>
    </div>
  )
}

const billingBadge = (missingFields: string[]) =>
  missingFields.length > 0 ? { label: 'À compléter', color: 'yellow' } : { label: 'Complet', color: 'green' }

export default function ContractShowPage({
  contract,
  orders,
  missing_fields,
  current_organization,
}: ContractShowPageProps) {
  const editPath = `/organizations/${current_organization?.slug}/contracts/${contract.id}/edit`
  const billingAddress = [contract.street_address, [contract.postal_code, contract.city].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ')
  const badge = billingBadge(missing_fields)

  const handleVerifyAddress = () =>
    router.post(`/organizations/${current_organization?.slug}/contracts/${contract.id}/electronic_address_verification`)

  return (
    <>
      <Head title={`Contrat: ${contract.customer_name} - ${current_organization?.name}`} />

      <Stack gap="xl">
        <Group justify="space-between">
          <Button
            component={Link}
            href={`/organizations/${current_organization?.slug}/contracts`}
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            color="gray"
          >
            Retour aux contrats
          </Button>

          <Group>
            <Button
              component={Link}
              href={`/organizations/${current_organization?.slug}/contracts/${contract.id}/catalog`}
              variant="light"
              color="grape"
              leftSection={<IconBook size={16} />}
            >
              Catalogue du contrat
            </Button>

            <Button
              component={Link}
              href={`/organizations/${current_organization?.slug}/contracts/${contract.id}/edit`}
              variant="light"
              leftSection={<IconPencil size={16} />}
            >
              Modifier le contrat
            </Button>
          </Group>
        </Group>

        <Paper withBorder radius="md" p="xl">
          <Stack gap="xs">
            <Group justify="space-between" align="start">
              <Title order={2}>{contract.customer_name}</Title>
              <Badge variant="light" color={contract.collected_by_provider ? 'blue' : 'gray'}>
                {contract.collected_by_provider ? 'Collecte coursier' : 'Sur place'}
              </Badge>
            </Group>

            <Group gap="xl" mt="md">
              {contract.customer_phone_number && (
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                    Téléphone
                  </Text>
                  <Text size="lg">{contract.customer_phone_number}</Text>
                </div>
              )}

              {contract.customer_email && (
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                    Email
                  </Text>
                  <Text size="lg">{contract.customer_email}</Text>
                </div>
              )}
            </Group>

            {contract.customer_address && (
              <Box mt="md">
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                  Adresse
                </Text>
                <Text size="lg">{contract.customer_address}</Text>
              </Box>
            )}
          </Stack>
        </Paper>

        <Paper withBorder radius="md" p="xl">
          <Stack gap="xs">
            <Group justify="space-between" align="start">
              <Title order={3}>Facturation</Title>
              <Badge variant="light" color={badge.color}>
                {badge.label}
              </Badge>
            </Group>
            <Group gap="xl" mt="md">
              <Detail label="Raison sociale" value={contract.legal_name} />
              <Detail label="SIREN" value={contract.siren} />
              <Detail label="N° de TVA" value={contract.vat_number} />
            </Group>
            <Group gap="xl" mt="md" align="flex-start">
              <Detail label="Adresse de facturation" value={billingAddress} />
              <Stack gap={4}>
                <Detail label="Adresse électronique" value={contract.electronic_address} />
                {contract.electronic_address && current_organization?.e_invoicing_available && (
                  <Group gap="xs">
                    <ElectronicAddressBadge status={contract.electronic_address_status} />
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      leftSection={<IconRefresh size={12} />}
                      onClick={handleVerifyAddress}
                    >
                      Vérifier
                    </Button>
                  </Group>
                )}
              </Stack>
            </Group>
            {missing_fields.length > 0 && (
              <Alert variant="light" color="yellow" icon={<IconAlertTriangle />} mt="md">
                Pour la facture électronique, il manque : {missing_fields.join(', ')}.{' '}
                <Anchor component={Link} href={editPath}>
                  Compléter le contrat
                </Anchor>
              </Alert>
            )}
          </Stack>
        </Paper>

        <Stack gap="md">
          <Title order={3}>Historique des commandes</Title>

          {orders.length > 0 ? (
            <Paper withBorder radius="md" p="md">
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>N° Commande</Table.Th>
                    <Table.Th>Date</Table.Th>
                    <Table.Th>Statut</Table.Th>
                    <Table.Th align="right">Prix Total</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {orders.map((order) => (
                    <Table.Tr
                      key={order.id}
                      onClick={() => router.get(`/organizations/${current_organization?.slug}/orders/${order.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Table.Td>#{order.id}</Table.Td>
                      <Table.Td>{dayjs(order.created_at).format('DD/MM/YYYY')}</Table.Td>
                      <Table.Td>
                        <Badge color={statusColors[order.status]} variant="light">
                          {formatStatus(order)}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text fw={700}>{Number(order.price).toFixed(2)}€</Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Paper>
          ) : (
            <Paper
              py={60}
              px="md"
              radius="lg"
              style={{
                border: '1px dashed var(--mantine-color-gray-3)',
                textAlign: 'center',
                backgroundColor: 'transparent',
              }}
            >
              <Stack align="center">
                <IconShoppingCart size={40} color="var(--mantine-color-gray-5)" />
                <Text c="dimmed">Aucune commande pour ce contrat</Text>
              </Stack>
            </Paper>
          )}
        </Stack>
      </Stack>
    </>
  )
}
